/**
 * The timezones a schedule is likely to need, and how they are labelled.
 *
 * A schedule stores an IANA name — `Asia/Kolkata` — because that is the only
 * thing that survives daylight saving: an offset is what a zone happens to be
 * today, not what it is. The label leads with the offset, since that is what
 * answers "when will this actually fire?".
 *
 * **This list is deliberately short.** `Intl.supportedValuesOf('timeZone')`
 * returns 418 zones; building labels for all of them cost ~120ms and rendering
 * them into a dropdown froze the tap. Nearly all of that is zones nobody here
 * will schedule against — `America/Argentina/Catamarca` and its neighbours. A
 * curated list is instant on both counts, and needs no caching or virtual
 * scrolling to stay that way.
 *
 * Adding one is a line below. The offsets are still computed at runtime rather
 * than written down, because a hard-coded offset is wrong twice a year in every
 * zone that observes daylight saving.
 */

export interface ZoneOption {
  /** The IANA name, which is what is stored. */
  value: string;
  /** e.g. "UTC+05:30 · Asia/Kolkata · IST" */
  label: string;
  offsetMinutes: number;
}

/**
 * Offered in the picker. Ordered west to east at render, not here.
 *
 * **One entry per distinct behaviour, not per city.** Paris, Berlin, Madrid,
 * Rome, Amsterdam, Brussels, Zurich, Stockholm and Warsaw are one zone wearing
 * nine names: same offset, same daylight-saving dates, same answer to "when
 * does this fire". Listing all nine made the picker look full of duplicates,
 * because it was.
 *
 * So each entry stands for a way of keeping time, and the city is just the
 * label the browser knows it by. Adding a specific city is one line here — the
 * reason not to is that it would sit beside an identical one.
 */
export const CURATED_ZONES = [
  'UTC',

  // The Americas
  'Pacific/Honolulu', 'America/Anchorage', 'America/Los_Angeles', 'America/Phoenix',
  'America/Denver', 'America/Mexico_City', 'America/Chicago', 'America/Bogota',
  'America/New_York', 'America/Halifax', 'America/Sao_Paulo', 'America/Santiago',

  // Europe and Africa
  'Europe/London', 'Africa/Lagos', 'Europe/Paris', 'Africa/Johannesburg',
  'Europe/Athens', 'Europe/Moscow',

  // The Gulf, the subcontinent and central Asia
  'Asia/Tehran', 'Asia/Dubai', 'Asia/Kabul', 'Asia/Karachi', 'Asia/Kolkata',
  'Asia/Kathmandu', 'Asia/Dhaka', 'Asia/Yangon',

  // Asia-Pacific
  'Asia/Bangkok', 'Asia/Singapore', 'Asia/Tokyo',
  'Australia/Darwin', 'Australia/Brisbane', 'Australia/Adelaide',
  'Australia/Sydney', 'Pacific/Auckland', 'Pacific/Fiji',
];

/**
 * One zone can have several names. `Asia/Calcutta` and `Asia/Kolkata` are the
 * same place; so are `Europe/Kiev` and `Europe/Kyiv`, `Asia/Rangoon` and
 * `Asia/Yangon`. Resolving both sides through Intl gives whichever name this
 * platform treats as canonical, so the comparison holds either way round.
 */
const canonical = (zone: string): string => {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: zone }).resolvedOptions().timeZone || zone;
  } catch {
    return zone;
  }
};

const sameZone = (a: string, b: string): boolean =>
  a === b || canonical(a) === canonical(b);

/**
 * The zone this browser is in — the sensible default for a new schedule.
 *
 * Returned as the curated name when the list already has this zone under
 * another spelling. A browser reporting `Asia/Calcutta` would otherwise have
 * it appended as an unknown, and the picker would show Calcutta and Kolkata as
 * two entries for one place.
 */
export const browserZone = (): string => {
  try {
    const reported = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    return CURATED_ZONES.find((z) => sameZone(z, reported)) || reported;
  } catch {
    return 'UTC';
  }
};

/** "+05:30", "-08:00", "+00:00". */
export const formatOffset = (minutes: number): string => {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
};

/**
 * Locales asked, in order, for a zone's abbreviation.
 *
 * No single locale names them all: `en-IN` gives IST and GST, `en-GB` gives
 * BST, `en-US` gives EDT — and each returns the bare offset for the zones it
 * has no name for. Asking three costs nothing on a list of forty, and asking
 * one would silently drop the abbreviation for two thirds of them.
 */
const ABBREVIATION_LOCALES = ['en-IN', 'en-GB', 'en-US'];

const timeZoneName = (zone: string, at: Date, locale: string, style: 'short' | 'longOffset'): string => {
  try {
    return new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: style })
      .formatToParts(at).find((p) => p.type === 'timeZoneName')?.value || '';
  } catch {
    return '';
  }
};

/** Minutes ahead of UTC, read out of a "GMT+5:30" / "GMT-03:00" / "GMT" name. */
const parseGmtOffset = (name: string): number | null => {
  if (/^GMT$/i.test(name.trim())) return 0;
  const m = /^GMT([+-])(\d{1,2})(?::(\d{2}))?$/.exec(name.trim());
  if (!m) return null;
  const minutes = Number(m[2]) * 60 + Number(m[3] || 0);
  return m[1] === '-' ? -minutes : minutes;
};

/** How far ahead of UTC a zone is right now, in minutes. */
export const offsetMinutesOf = (zone: string, at: Date = new Date()): number =>
  parseGmtOffset(timeZoneName(zone, at, 'en-US', 'longOffset')) ?? 0;

/** IST, GST, BST — when any of the locales has a name for it rather than an offset. */
const abbreviationOf = (zone: string, at: Date): string => {
  for (const locale of ABBREVIATION_LOCALES) {
    const name = timeZoneName(zone, at, locale, 'short');
    if (/^[A-Z]{2,5}$/.test(name)) return name;
  }
  return '';
};

const describe = (zone: string, at: Date): ZoneOption => {
  const offsetMinutes = offsetMinutesOf(zone, at);
  const abbr = abbreviationOf(zone, at);
  return {
    value: zone,
    offsetMinutes,
    label: `UTC${formatOffset(offsetMinutes)} · ${zone}${abbr ? ` · ${abbr}` : ''}`,
  };
};

/**
 * The curated zones, ordered west to east.
 *
 * `include` adds a zone that is already stored but not on the list. A schedule
 * seeded or migrated with something else must still show what it is set to,
 * rather than rendering an empty picker over a value that is really there —
 * and worse, saving that emptiness back.
 */
export const zoneOptions = (include?: string | null, at: Date = new Date()): ZoneOption[] => {
  // Compared by zone, not by spelling: appending a stored `Asia/Calcutta`
  // beside the curated `Asia/Kolkata` would offer the same place twice.
  const names = include && !CURATED_ZONES.some((z) => sameZone(z, include))
    ? [...CURATED_ZONES, include]
    : CURATED_ZONES;
  return names
    .map((zone) => describe(zone, at))
    .sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.value.localeCompare(b.value));
};

/** One zone's label, for a value that is already stored. */
export const zoneLabel = (zone?: string | null, at: Date = new Date()): string =>
  (zone ? describe(zone, at).label : '-');
